import { HttpClient } from '@angular/common/http'
import { Injectable, inject, signal } from '@angular/core'
import { Observable, catchError, map, of, shareReplay } from 'rxjs'
import { environment } from '../../environments/environment'
import { AuthService } from '../auth/services/auth.service'
import { ApiResponse } from '../shared/interfaces/api.interface'
import { StatClipEvent } from '../shared/interfaces/stat-clips.interface'

// Goal Analysis (Andreas, 30/10/2026): every goal told the way a coach tells it -- how the move
// started and how the goal was created. Admins first; anyone else through the Goal Analysis
// add-on, so the server decides who sees the tab (GET /goal-analysis/access).

/** How the move started. */
export type GoalCategory = 'org' | 'counter' | 'high' | 'set' | 'pen' | 'other'

export interface GoalAnalysisGoal {
  event_key: string
  match_id: string
  season_name: string
  team_id: string
  side: string
  home_team_name: string
  away_team_name: string
  team_name: string
  opponent_name: string
  scorer: string
  is_own_goal: boolean
  minute: number
  category: GoalCategory
  /** How the ball was won (counter, high regain), or the set piece and its phase. */
  sub: string
  created: string
  created_detail: string
  seconds: number
  passes: number
  /** Where the ball was won, the shot was taken and the assist was played: metres from the
   *  centre spot, the scoring team attacking +x (105 × 68 pitch). */
  won_x: number | null
  won_y: number | null
  shot_x: number | null
  shot_y: number | null
  assist_player: string
  /** The pass to the assister (SciSports PreAssist); empty when none was tagged. */
  pre_assist_player: string
  /** Platform player ids (phase 3): group by player, not by name. Empty when not known. */
  scorer_id?: string
  assist_player_id?: string
  pre_assist_player_id?: string
  body_part: string
  /** The shot's xG. */
  xg: number | null
  part_name: string
  competition_id: string
  /** Kick-off, ISO; sent since phase 2 (02/11/2026). */
  match_date?: string | null
  /** Null when the match has no video or the viewer may not open it. */
  clip: StatClipEvent | null
}

export interface GoalAnalysisResponse {
  season: string
  goals: GoalAnalysisGoal[]
}

export const CATEGORY_ORDER: GoalCategory[] = ['org', 'counter', 'high', 'set', 'pen', 'other']

export const CATEGORY_LABEL: Record<GoalCategory, string> = {
  org: 'Organised attack',
  counter: 'Counter-attack',
  high: 'High regain',
  set: 'Set piece',
  pen: 'Penalty',
  other: 'Own goal / not classified',
}

/** One line each, as the video check agreed them (Andreas, 30/10/2026). */
export const CATEGORY_HINT: Record<GoalCategory, string> = {
  org: 'The team built the move with the ball.',
  counter: 'Ball won outside 35 m from goal, scored within 17 s.',
  high: 'Ball won within 35 m of goal (or from their goal kick), scored within 17 s.',
  set: 'Corner, free kick or throw-in in the final third, up to its second phase.',
  pen: 'A penalty, or a goal straight after one.',
  other: 'No move to read in the data.',
}

/** One colour per type, the same on every panel (Andreas, 30/10/2026: colour that means
 *  something). Platform palette only; red and green stay for results. */
// Softened a touch with white (Andreas, 31/10/2026: "a bit lighter"); still the palette's hues.
const soft = (c: string) => `color-mix(in srgb, ${c} 72%, white)`
export const CATEGORY_COLOR: Record<GoalCategory, string> = {
  org: soft('var(--accent-primary)'),
  counter: soft('var(--accent-orange)'),
  high: soft('var(--accent-purple)'),
  set: soft('var(--accent-amber)'),
  pen: soft('var(--scios-text-muted)'),
  other: 'var(--scios-border)',
}

export const CREATED_ORDER = [
  'Cross', 'Cut-back', 'Pass into the box', 'Pass inside the box', 'Long-range shot',
  'Rebound', 'Set-piece delivery', 'Finish after a regain', 'Solo',
]

@Injectable({ providedIn: 'root' })
export class GoalAnalysisService {
  private http = inject(HttpClient)
  private auth = inject(AuthService)
  private base = environment.apiUrl

  /** Whether this account sees the Goal Analysis tabs; false until the server says yes. */
  readonly allowed = signal(false)
  private access$?: Observable<boolean>
  private accessFor = ''

  /** Asks the server once per signed-in account; the tab guard and the navs share the answer. */
  checkAccess (): Observable<boolean> {
    const who = this.auth.currentUser()?.id ?? ''
    if (who !== this.accessFor) {
      this.access$ = undefined
      this.allowed.set(false)
      this.accessFor = who
    }
    this.access$ ??= this.http.get<ApiResponse<{ allowed: boolean }>>(`${this.base}/goal-analysis/access`).pipe(
      map(r => !!r.data?.allowed),
      catchError(() => of(false)),
      shareReplay(1),
    )
    this.access$.subscribe(ok => this.allowed.set(ok))
    return this.access$
  }

  competition (competitionId: string, season: string): Observable<GoalAnalysisResponse> {
    return this.get(`${this.base}/competitions/${competitionId}/goal-analysis`, season)
  }

  /** The team's goals scored and conceded. */
  team (teamId: string, season: string): Observable<GoalAnalysisResponse> {
    return this.get(`${this.base}/teams/${teamId}/goal-analysis`, season)
  }

  /** One match's goals (the match page, phase 3); the season comes back with them. */
  match (fixtureId: string): Observable<GoalAnalysisResponse> {
    return this.http.get<ApiResponse<GoalAnalysisResponse>>(`${this.base}/fixtures/${fixtureId}/goal-analysis`).pipe(map(r => r.data))
  }

  private get (url: string, season: string): Observable<GoalAnalysisResponse> {
    return this.http.get<ApiResponse<GoalAnalysisResponse>>(`${url}?${new URLSearchParams({ season })}`).pipe(map(r => r.data))
  }
}
